import React, { useState, useEffect } from 'react';
import api from '../services/api';
import { UserPlus, Trash2, Edit, UserX, UserCheck, Shield } from 'lucide-react';

const UserManagementPortal: React.FC = () => {
  const [users, setUsers] = useState<any[]>([]);
  const [pendingUsers, setPendingUsers] = useState<any[]>([]);
  const [activeTab, setActiveTab] = useState<'all' | 'pending'>('all');
  const [loading, setLoading] = useState(true);
  const [showAddModal, setShowAddModal] = useState(false);
  const [editingUser, setEditingUser] = useState<any | null>(null);

  const [newUser, setNewUser] = useState({
    name: '',
    email: '',
    password: '',
    phone: '',
    role: 'admin',
    thinkTankSector: '',
    thinkTankSpecialty: ''
  });

  const fetchUsers = async () => {
    try {
      const response = await api.get('/auth/users');
      setUsers(response.data);
    } catch (err) {
      console.error('Error fetching users:', err);
    } finally {
      setLoading(false);
    }
  };

  const fetchPendingUsers = async () => {
    try {
      const res = await api.get('/admin/pending-users');
      setPendingUsers(Array.isArray(res.data) ? res.data : []);
    } catch (err) {
      console.error('Error fetching pending users:', err);
      setPendingUsers([]);
    }
  };

  useEffect(() => {
    fetchUsers();
    fetchPendingUsers();
  }, []);

  const handleApproveUser = async (id: string) => {
    try {
      await api.post(`/admin/approve-user/${id}`);
      alert('✅ User registration approved and account activated!');
      fetchPendingUsers();
      fetchUsers();
    } catch (err: any) {
      alert(err.response?.data?.message || 'Error approving user');
    }
  };

  const handleRejectUser = async (id: string) => {
    const comments = window.prompt('Specify reason/comments for registration rejection (sent to applicant via email):', 'Uploaded credentials could not be verified.');
    if (comments === null) return;
    try {
      await api.post(`/admin/reject-user/${id}`, { comments });
      alert('⚠️ Registration rejected and email alert dispatched.');
      fetchPendingUsers();
      fetchUsers();
    } catch (err: any) {
      alert(err.response?.data?.message || 'Error rejecting user');
    }
  };

  const handleCreateUser = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      await api.post('/admin/users', newUser);
      alert('✅ Internal User created successfully!');
      setShowAddModal(false);
      setNewUser({ name: '', email: '', password: '', phone: '', role: 'admin', thinkTankSector: '', thinkTankSpecialty: '' });
      fetchUsers();
    } catch (err: any) {
      alert(err.response?.data?.message || 'Error creating user account');
    }
  };

  const handleEditUserSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!editingUser) return;
    try {
      await api.put(`/admin/users/${editingUser.id}`, editingUser);
      alert(`✅ User profile for ${editingUser.name} updated successfully!`);
      setEditingUser(null);
      fetchUsers();
    } catch (err: any) {
      alert(err.response?.data?.message || `Error updating user profile for ${editingUser.name}`);
    }
  };

  const handleUpdateStatus = async (id: string, status: string) => {
    try {
      await api.patch(`/admin/users/${id}/status`, { status });
      alert(`✅ User status updated to ${status.replace('_', ' ').toUpperCase()}`);
      fetchUsers();
    } catch (err: any) {
      alert(err.response?.data?.message || 'Error updating user status');
    }
  };

  const handleDeleteUser = async (id: string) => {
    if (!window.confirm('⚠️ Are you sure you want to permanently delete this user from the system?')) return;
    try {
      await api.delete(`/admin/users/${id}`);
      alert('✅ User deleted successfully.');
      fetchUsers();
    } catch (err: any) {
      alert(err.response?.data?.message || 'Error deleting user account');
    }
  };

  if (loading) return <div style={{ padding: '2rem', textAlign: 'center', color: '#1a365d' }}>Loading Users & User Access Control...</div>;

  return (
    <div style={{ padding: '1rem 0', maxWidth: '1350px', margin: '0 auto', width: '100%', boxSizing: 'border-box', fontFamily: '"Outfit", sans-serif' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.5rem', flexWrap: 'wrap', gap: '1rem', width: '100%', boxSizing: 'border-box' }}>
        <div>
          <h2 style={{ fontSize: '1.25rem', fontWeight: '800', color: 'inherit', margin: 0, lineHeight: '1.3' }}>🛡️ User Access & Role Control Vault</h2>
          <p style={{ fontSize: '0.82rem', color: '#64748b', margin: '0.2rem 0 0 0' }}>Manage system accounts, edit user roles, verify new registration approvals, and inspect credentials.</p>
        </div>
        <div style={{ display: 'flex', gap: '0.6rem', alignItems: 'center', flexWrap: 'wrap' }}>
          <button 
            onClick={() => { fetchUsers(); fetchPendingUsers(); }}
            style={{ padding: '0.6rem 1rem', backgroundColor: '#f1f5f9', color: '#0f172a', border: '1px solid #cbd5e1', borderRadius: '8px', cursor: 'pointer', fontWeight: 'bold', fontSize: '0.85rem' }}
          >
            🔄 Refresh
          </button>
          <button 
            onClick={() => setShowAddModal(true)}
            style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', padding: '0.6rem 1.25rem', backgroundColor: '#0f172a', color: '#f59e0b', border: '1px solid rgba(245, 158, 11, 0.5)', borderRadius: '8px', cursor: 'pointer', fontWeight: 'bold', fontSize: '0.85rem', whiteSpace: 'nowrap' }}
          >
            <UserPlus size={16} /> Add New Internal Staff / Expert
          </button>
        </div>
      </div>

      {/* VIEW TABS: ALL USERS vs PENDING REGISTRATION APPROVALS */}
      <div style={{ display: 'flex', gap: '0.75rem', marginBottom: '1.5rem', flexWrap: 'wrap' }}>
        <button
          onClick={() => setActiveTab('all')}
          style={{
            padding: '0.65rem 1.25rem',
            borderRadius: '8px',
            border: activeTab === 'all' ? '2px solid #0f172a' : '1px solid #cbd5e1',
            backgroundColor: activeTab === 'all' ? '#0f172a' : '#f8fafc',
            color: activeTab === 'all' ? '#f59e0b' : '#475569',
            fontWeight: '800',
            fontSize: '0.88rem',
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            gap: '0.5rem'
          }}
        >
          <Shield size={16} /> All Active & Registered Accounts ({users.length})
        </button>

        <button
          onClick={() => setActiveTab('pending')}
          style={{
            padding: '0.65rem 1.25rem',
            borderRadius: '8px',
            border: activeTab === 'pending' ? '2px solid #dc2626' : '1px solid #cbd5e1',
            backgroundColor: activeTab === 'pending' ? '#dc2626' : '#f8fafc',
            color: activeTab === 'pending' ? 'white' : '#475569',
            fontWeight: '800',
            fontSize: '0.88rem',
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            gap: '0.5rem'
          }}
        >
          <span>⏳ Pending Registration Approvals ({pendingUsers.length})</span>
          {pendingUsers.length > 0 && (
            <span style={{ backgroundColor: activeTab === 'pending' ? 'white' : '#dc2626', color: activeTab === 'pending' ? '#dc2626' : 'white', fontSize: '0.7rem', padding: '0.15rem 0.45rem', borderRadius: '10px', fontWeight: '800' }}>
              ACTION REQUIRED
            </span>
          )}
        </button>
      </div>

      {/* PENDING APPROVALS VIEW */}
      {activeTab === 'pending' && (
        <div style={{ backgroundColor: 'white', borderRadius: '12px', boxShadow: '0 4px 15px rgba(0,0,0,0.05)', border: '1px solid #e2e8f0', padding: '1.5rem', marginBottom: '2rem' }}>
          <h3 style={{ fontSize: '1.1rem', fontWeight: '800', color: '#dc2626', margin: '0 0 1rem 0' }}>
            ⏳ New User Registrations Awaiting Verification & Approval ({pendingUsers.length})
          </h3>

          {pendingUsers.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '3rem 1rem', color: '#64748b' }}>
              <div style={{ fontSize: '2rem', marginBottom: '0.5rem' }}>🎉</div>
              <div style={{ fontWeight: 'bold' }}>All clear! No user registrations pending approval.</div>
            </div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
              {pendingUsers.map(pu => (
                <div key={pu.id} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', backgroundColor: '#fff1f2', border: '1px solid #fecdd3', borderRadius: '10px', padding: '1.25rem', flexWrap: 'wrap', gap: '1rem' }}>
                  <div style={{ flex: 1, minWidth: '280px' }}>
                    <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center', marginBottom: '0.35rem' }}>
                      <span style={{ backgroundColor: '#dc2626', color: 'white', fontSize: '0.7rem', padding: '0.15rem 0.5rem', borderRadius: '4px', fontWeight: '800', textTransform: 'uppercase' }}>
                        {pu.role}
                      </span>
                      <strong style={{ fontSize: '1.05rem', color: '#0f172a' }}>{pu.name}</strong>
                    </div>
                    <div style={{ fontSize: '0.85rem', color: '#475569', lineHeight: '1.5' }}>
                      <div>📧 Email: <strong style={{ color: '#0284c7' }}>{pu.email}</strong> | 📱 Phone: <strong>{pu.phone || 'N/A'}</strong></div>
                      <div>🪪 CNIC: <strong>{pu.cnic || 'N/A'}</strong> {pu.cnicExpiry ? `(Expires: ${pu.cnicExpiry})` : ''}</div>
                      {pu.barCouncilId && (
                        <div>⚖️ Bar License ID: <strong style={{ color: '#d97706' }}>{pu.barCouncilId}</strong> {pu.barCouncilExpiry ? `(Expires: ${pu.barCouncilExpiry})` : ''}</div>
                      )}
                      <div style={{ fontSize: '0.78rem', color: '#94a3b8', marginTop: '0.25rem' }}>Registered on: {new Date(pu.createdAt).toLocaleString()}</div>
                    </div>
                  </div>

                  <div style={{ display: 'flex', gap: '0.6rem', alignItems: 'center' }}>
                    <button
                      onClick={() => handleApproveUser(pu.id)}
                      style={{ padding: '0.6rem 1.25rem', backgroundColor: '#16a34a', color: 'white', border: 'none', borderRadius: '6px', fontWeight: '800', fontSize: '0.85rem', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '0.35rem' }}
                    >
                      <UserCheck size={16} /> Approve & Activate
                    </button>
                    <button
                      onClick={() => handleRejectUser(pu.id)}
                      style={{ padding: '0.6rem 1rem', backgroundColor: '#fee2e2', color: '#dc2626', border: '1px solid #fca5a5', borderRadius: '6px', fontWeight: '800', fontSize: '0.85rem', cursor: 'pointer' }}
                    >
                      Reject
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* ALL USERS VIEW */}
      {activeTab === 'all' && (
      <div style={{ overflowX: 'auto', backgroundColor: 'white', borderRadius: '12px', boxShadow: '0 4px 15px rgba(0,0,0,0.05)', border: '1px solid #e2e8f0', width: '100%', boxSizing: 'border-box' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', textAlign: 'left', minWidth: '850px' }}>
          <thead style={{ backgroundColor: '#f8fafc', borderBottom: '2px solid #e2e8f0' }}>
            <tr>
              <th style={{ padding: '0.85rem 1rem', fontSize: '0.82rem', color: '#475569', fontWeight: '800' }}>User Credentials</th>
              <th style={{ padding: '0.85rem 1rem', fontSize: '0.82rem', color: '#475569', fontWeight: '800' }}>System Role</th>
              <th style={{ padding: '0.85rem 1rem', fontSize: '0.82rem', color: '#475569', fontWeight: '800' }}>Status</th>
              <th style={{ padding: '0.85rem 1rem', fontSize: '0.82rem', color: '#475569', fontWeight: '800' }}>Think Tank Specialty</th>
              <th style={{ padding: '0.85rem 1rem', fontSize: '0.82rem', color: '#475569', textAlign: 'center', fontWeight: '800' }}>User Actions</th>
            </tr>
          </thead>
          <tbody>
            {users.map((user) => (
              <tr key={user.id} style={{ borderBottom: '1px solid #edf2f7' }}>
                <td style={{ padding: '1rem' }}>
                  <div style={{ fontWeight: 'bold', color: '#1e293b', fontSize: '0.95rem' }}>{user.name}</div>
                  <div style={{ fontSize: '0.8rem', color: '#64748b' }}>{user.email} | {user.phone || 'No Phone'}</div>
                </td>
                <td style={{ padding: '1rem' }}>
                  <span style={{ fontSize: '0.78rem', backgroundColor: user.role === 'superuser' ? '#fef3c7' : '#ebf8ff', color: user.role === 'superuser' ? '#92400e' : '#2b6cb0', padding: '0.25rem 0.6rem', borderRadius: '9999px', fontWeight: 'bold', textTransform: 'capitalize' }}>
                    {user.role?.replace('_', ' ')}
                  </span>
                </td>
                <td style={{ padding: '1rem' }}>
                  <span style={{ 
                    fontSize: '0.78rem', 
                    padding: '0.2rem 0.6rem',
                    borderRadius: '9999px',
                    backgroundColor: user.status === 'active' ? '#dcfce7' : '#fee2e2',
                    color: user.status === 'active' ? '#166534' : '#991b1b',
                    fontWeight: 'bold'
                  }}>
                    {user.status?.replace('_', ' ').toUpperCase() || 'ACTIVE'}
                  </span>
                </td>
                <td style={{ padding: '1rem', fontSize: '0.8rem', color: '#475569' }}>
                  {user.thinkTankSector ? (
                    <div>
                      <div><strong>Sector:</strong> {user.thinkTankSector}</div>
                      <div><strong>Specialty:</strong> {user.thinkTankSpecialty || 'General'}</div>
                    </div>
                  ) : '-'}
                </td>
                <td style={{ padding: '1rem' }}>
                  <div style={{ display: 'flex', gap: '0.4rem', justifyContent: 'center' }}>
                    
                    {/* EDIT USER BUTTON */}
                    <button 
                      onClick={() => setEditingUser({ ...user })} 
                      title="Edit User Profile" 
                      style={{ padding: '0.4rem 0.75rem', border: '1px solid #cbd5e1', backgroundColor: '#f8fafc', cursor: 'pointer', borderRadius: '6px', fontSize: '0.78rem', fontWeight: 'bold', color: '#2563eb', display: 'flex', alignItems: 'center', gap: '0.3rem' }}
                    >
                      <Edit size={14} /> Edit
                    </button>

                    {/* SUSPEND / ACTIVATE BUTTON */}
                    {user.status === 'active' ? (
                      <button 
                        onClick={() => handleUpdateStatus(user.id, 'suspended_temporary')} 
                        title="Suspend User Access" 
                        style={{ padding: '0.4rem 0.75rem', border: '1px solid #fca5a5', backgroundColor: '#fef2f2', cursor: 'pointer', borderRadius: '6px', fontSize: '0.78rem', fontWeight: 'bold', color: '#dc2626', display: 'flex', alignItems: 'center', gap: '0.3rem' }}
                      >
                        <UserX size={14} /> Suspend
                      </button>
                    ) : (
                      <button 
                        onClick={() => handleUpdateStatus(user.id, 'active')} 
                        title="Activate User Access" 
                        style={{ padding: '0.4rem 0.75rem', border: '1px solid #86efac', backgroundColor: '#f0fdf4', cursor: 'pointer', borderRadius: '6px', fontSize: '0.78rem', fontWeight: 'bold', color: '#16a34a', display: 'flex', alignItems: 'center', gap: '0.3rem' }}
                      >
                        <UserCheck size={14} /> Activate
                      </button>
                    )}

                    {/* DELETE USER BUTTON */}
                    <button 
                      onClick={() => handleDeleteUser(user.id)} 
                      title="Delete User Account" 
                      style={{ padding: '0.4rem 0.75rem', border: '1px solid #e2e8f0', backgroundColor: '#f1f5f9', cursor: 'pointer', borderRadius: '6px', fontSize: '0.78rem', fontWeight: 'bold', color: '#475569', display: 'flex', alignItems: 'center', gap: '0.3rem' }}
                    >
                      <Trash2 size={14} color="#dc2626" /> Delete
                    </button>

                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      )}

      {/* EDIT USER MODAL */}
      {editingUser && (
        <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: 'rgba(0,0,0,0.6)', display: 'flex', justifyContent: 'center', alignItems: 'center', zIndex: 9999 }}>
          <div style={{ backgroundColor: 'white', padding: '2rem', borderRadius: '12px', width: '100%', maxWidth: '550px', boxShadow: '0 20px 25px -5px rgba(0,0,0,0.3)' }}>
            <h3 style={{ marginBottom: '1.25rem', color: '#1a365d', fontWeight: '800' }}>✏️ Edit User Profile & System Role</h3>
            <form onSubmit={handleEditUserSubmit}>
              <div style={formGroupStyle}>
                <label style={{ fontWeight: 'bold', fontSize: '0.85rem' }}>Full Name</label>
                <input type="text" value={editingUser.name} onChange={(e) => setEditingUser({...editingUser, name: e.target.value})} required style={inputStyle} />
              </div>

              <div style={formGroupStyle}>
                <label style={{ fontWeight: 'bold', fontSize: '0.85rem' }}>Email Address</label>
                <input type="email" value={editingUser.email} onChange={(e) => setEditingUser({...editingUser, email: e.target.value})} required style={inputStyle} />
              </div>

              <div style={formGroupStyle}>
                <label style={{ fontWeight: 'bold', fontSize: '0.85rem' }}>Phone Number</label>
                <input type="text" value={editingUser.phone || ''} onChange={(e) => setEditingUser({...editingUser, phone: e.target.value})} style={inputStyle} />
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
                <div style={formGroupStyle}>
                  <label style={{ fontWeight: 'bold', fontSize: '0.85rem' }}>Assigned System Role</label>
                  <select value={editingUser.role} onChange={(e) => setEditingUser({...editingUser, role: e.target.value})} style={inputStyle}>
                    <option value="client">Client / Citizen</option>
                    <option value="practitioner">Legal Practitioner / Advocate</option>
                    <option value="law_firm">Law Firm Partnership</option>
                    <option value="associate">Associate Lawyer</option>
                    <option value="think_tank_member">Think Tank Expert</option>
                    <option value="senior_partner">Senior Partner</option>
                    <option value="admin">Administrator</option>
                    <option value="administrator">Super Administrator</option>
                    <option value="superuser">Superuser (Advocate NajeebUllah)</option>
                  </select>
                </div>

                <div style={formGroupStyle}>
                  <label style={{ fontWeight: 'bold', fontSize: '0.85rem' }}>Account Status</label>
                  <select value={editingUser.status} onChange={(e) => setEditingUser({...editingUser, status: e.target.value})} style={inputStyle}>
                    <option value="active">ACTIVE</option>
                    <option value="suspended_temporary">SUSPENDED TEMPORARY</option>
                    <option value="suspended_permanent">SUSPENDED PERMANENT</option>
                  </select>
                </div>
              </div>

              {editingUser.role === 'think_tank_member' && (
                <>
                  <div style={formGroupStyle}>
                    <label style={{ fontWeight: 'bold', fontSize: '0.85rem' }}>Think Tank Sector</label>
                    <input type="text" value={editingUser.thinkTankSector || ''} onChange={(e) => setEditingUser({...editingUser, thinkTankSector: e.target.value})} style={inputStyle} />
                  </div>
                  <div style={formGroupStyle}>
                    <label style={{ fontWeight: 'bold', fontSize: '0.85rem' }}>Think Tank Specialty</label>
                    <input type="text" value={editingUser.thinkTankSpecialty || ''} onChange={(e) => setEditingUser({...editingUser, thinkTankSpecialty: e.target.value})} style={inputStyle} />
                  </div>
                </>
              )}

              <div style={{ display: 'flex', gap: '1rem', marginTop: '1.5rem' }}>
                <button type="submit" style={{ flex: 1, padding: '0.75rem', backgroundColor: '#2563eb', color: 'white', border: 'none', borderRadius: '6px', cursor: 'pointer', fontWeight: 'bold' }}>Save Changes</button>
                <button type="button" onClick={() => setEditingUser(null)} style={{ flex: 1, padding: '0.75rem', backgroundColor: '#e2e8f0', border: 'none', borderRadius: '6px', cursor: 'pointer', fontWeight: 'bold' }}>Cancel</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* ADD NEW USER MODAL */}
      {showAddModal && (
        <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: 'rgba(0,0,0,0.6)', display: 'flex', justifyContent: 'center', alignItems: 'center', zIndex: 9999 }}>
          <div style={{ backgroundColor: 'white', padding: '2rem', borderRadius: '12px', width: '100%', maxWidth: '500px', boxShadow: '0 20px 25px -5px rgba(0,0,0,0.3)' }}>
            <h3 style={{ marginBottom: '1.5rem', color: '#1a365d', fontWeight: '800' }}>Add New Internal User / Staff</h3>
            <form onSubmit={handleCreateUser}>
              <div style={formGroupStyle}>
                <label style={{ fontWeight: 'bold', fontSize: '0.85rem' }}>Full Name</label>
                <input type="text" value={newUser.name} onChange={(e) => setNewUser({...newUser, name: e.target.value})} required style={inputStyle} />
              </div>
              <div style={formGroupStyle}>
                <label style={{ fontWeight: 'bold', fontSize: '0.85rem' }}>Email Address</label>
                <input type="email" value={newUser.email} onChange={(e) => setNewUser({...newUser, email: e.target.value})} required style={inputStyle} />
              </div>
              <div style={formGroupStyle}>
                <label style={{ fontWeight: 'bold', fontSize: '0.85rem' }}>Initial Password</label>
                <input type="password" value={newUser.password} onChange={(e) => setNewUser({...newUser, password: e.target.value})} required style={inputStyle} />
              </div>
              <div style={formGroupStyle}>
                <label style={{ fontWeight: 'bold', fontSize: '0.85rem' }}>Assigned Role</label>
                <select value={newUser.role} onChange={(e) => setNewUser({...newUser, role: e.target.value})} style={inputStyle}>
                  <option value="admin">Administrator</option>
                  <option value="finance">Finance Officer</option>
                  <option value="think_tank_member">Think Tank Member</option>
                  <option value="senior_partner">Senior Partner</option>
                  <option value="practitioner">Legal Practitioner</option>
                </select>
              </div>

              {newUser.role === 'think_tank_member' && (
                <>
                  <div style={formGroupStyle}>
                    <label style={{ fontWeight: 'bold', fontSize: '0.85rem' }}>Legal Sector</label>
                    <input type="text" value={newUser.thinkTankSector} onChange={(e) => setNewUser({...newUser, thinkTankSector: e.target.value})} placeholder="e.g. Constitutional Law" style={inputStyle} />
                  </div>
                  <div style={formGroupStyle}>
                    <label style={{ fontWeight: 'bold', fontSize: '0.85rem' }}>Specialty</label>
                    <input type="text" value={newUser.thinkTankSpecialty} onChange={(e) => setNewUser({...newUser, thinkTankSpecialty: e.target.value})} placeholder="e.g. Supreme Court Appeals" style={inputStyle} />
                  </div>
                </>
              )}

              <div style={{ display: 'flex', gap: '1rem', marginTop: '1.5rem' }}>
                <button type="submit" style={{ flex: 1, padding: '0.75rem', backgroundColor: '#1a365d', color: 'white', border: 'none', borderRadius: '6px', cursor: 'pointer', fontWeight: 'bold' }}>Create User</button>
                <button type="button" onClick={() => setShowAddModal(false)} style={{ flex: 1, padding: '0.75rem', backgroundColor: '#e2e8f0', border: 'none', borderRadius: '6px', cursor: 'pointer', fontWeight: 'bold' }}>Cancel</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

const formGroupStyle = { marginBottom: '1rem' };
const inputStyle = { width: '100%', padding: '0.6rem', borderRadius: '6px', border: '1px solid #cbd5e1', fontSize: '0.9rem', outline: 'none' };

export default UserManagementPortal;
